/**
 * Market Analysis Form
 * TAM/SAM/SOM calculator and competition analysis
 */
import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketAnalysisForm = ({ planId }) => {
  const [analysis, setAnalysis] = useState({
    total_addressable_market: '',
    serviceable_addressable_market: '',
    serviceable_obtainable_market: '',
    market_description: '',
    market_growth_rate: '',
    competitive_advantage: '',
    target_customer_segments: []
  });
  const [competitors, setCompetitors] = useState([]);
  const [newCompetitor, setNewCompetitor] = useState({ name: '', market_share: '', strengths: '', weaknesses: '' });

  const addCompetitor = () => {
    setCompetitors([...competitors, newCompetitor]);
    setNewCompetitor({ name: '', market_share: '', strengths: '', weaknesses: '' });
  };

  const saveAnalysis = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/market-analysis`, {
        plan_id: planId,
        ...analysis,
        competitors: competitors
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Market analysis saved!');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="space-y-8 p-8">
      <div>
        <h2 className="text-3xl font-bold mb-2">Market Analysis</h2>
        <p className="text-gray-600">Define your target market and competitive landscape</p>
      </div>

      {/* TAM/SAM/SOM */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold mb-4">Market Size (TAM/SAM/SOM)</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              TAM (Total Addressable Market)
              <span className="text-gray-500 block text-xs">Total market demand in ETB</span>
            </label>
            <input
              type="number"
              value={analysis.total_addressable_market}
              onChange={(e) => setAnalysis({...analysis, total_addressable_market: e.target.value})}
              placeholder="e.g., 500000000"
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              SAM (Serviceable Addressable Market)
              <span className="text-gray-500 block text-xs">Market you can reach</span>
            </label>
            <input
              type="number"
              value={analysis.serviceable_addressable_market}
              onChange={(e) => setAnalysis({...analysis, serviceable_addressable_market: e.target.value})}
              placeholder="e.g., 100000000"
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              SOM (Serviceable Obtainable Market)
              <span className="text-gray-500 block text-xs">Your realistic target</span>
            </label>
            <input
              type="number"
              value={analysis.serviceable_obtainable_market}
              onChange={(e) => setAnalysis({...analysis, serviceable_obtainable_market: e.target.value})}
              placeholder="e.g., 20000000"
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded">
          <p className="text-sm text-blue-800">
            💡 <strong>Guidance:</strong> TAM ≥ SAM ≥ SOM. Your SOM should be realistic (typically 5-20% of SAM for new businesses)
          </p>
        </div>
      </div>

      {/* Market Description */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold mb-4">Market Overview</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Market Description</label>
            <textarea
              value={analysis.market_description}
              onChange={(e) => setAnalysis({...analysis, market_description: e.target.value})}
              placeholder="Describe your target market, customer needs, trends..."
              className="w-full border rounded px-3 py-2 h-32"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Annual Market Growth Rate (%)</label>
            <input
              type="number"
              step="0.1"
              value={analysis.market_growth_rate}
              onChange={(e) => setAnalysis({...analysis, market_growth_rate: e.target.value})}
              placeholder="e.g., 8.5"
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>
      </div>

      {/* Competition */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold mb-4">Competition Analysis</h3>
        
        {/* Competitors List */}
        {competitors.length > 0 && (
          <div className="mb-4 space-y-2">
            {competitors.map((comp, idx) => (
              <div key={idx} className="border rounded p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{comp.name}</div>
                    <div className="text-sm text-gray-600">Market Share: {comp.market_share}%</div>
                  </div>
                  <button
                    onClick={() => setCompetitors(competitors.filter((_, i) => i !== idx))}
                    className="text-red-600 text-sm"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Add Competitor Form */}
        <div className="border-2 border-dashed rounded-lg p-4">
          <h4 className="font-medium mb-3">Add Competitor</h4>
          <div className="grid grid-cols-2 gap-3">
            <input
              placeholder="Competitor name"
              value={newCompetitor.name}
              onChange={(e) => setNewCompetitor({...newCompetitor, name: e.target.value})}
              className="border rounded px-3 py-2"
            />
            <input
              placeholder="Market share (%)"
              type="number"
              value={newCompetitor.market_share}
              onChange={(e) => setNewCompetitor({...newCompetitor, market_share: e.target.value})}
              className="border rounded px-3 py-2"
            />
            <input
              placeholder="Their strengths"
              value={newCompetitor.strengths}
              onChange={(e) => setNewCompetitor({...newCompetitor, strengths: e.target.value})}
              className="border rounded px-3 py-2"
            />
            <input
              placeholder="Their weaknesses"
              value={newCompetitor.weaknesses}
              onChange={(e) => setNewCompetitor({...newCompetitor, weaknesses: e.target.value})}
              className="border rounded px-3 py-2"
            />
          </div>
          <button
            onClick={addCompetitor}
            disabled={!newCompetitor.name}
            className="mt-3 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-300"
          >
            + Add Competitor
          </button>
        </div>
      </div>

      {/* Competitive Advantage */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold mb-4">Your Competitive Advantage</h3>
        <textarea
          value={analysis.competitive_advantage}
          onChange={(e) => setAnalysis({...analysis, competitive_advantage: e.target.value})}
          placeholder="What makes your business different and better than competitors?"
          className="w-full border rounded px-3 py-2 h-32"
        />
      </div>

      <button
        onClick={saveAnalysis}
        className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium"
      >
        💾 Save Market Analysis
      </button>
    </div>
  );
};

export default MarketAnalysisForm;